import { useCallback, useRef, useState } from "react"

/**
 * Drag & drop de réordonnancement, fait-main en Pointer Events.
 *
 * Remplace le HTML5 natif (clunky : aucun retour visuel, largage à l'aveugle).
 * Ici la ligne attrapée se soulève et suit le pointeur, les autres lignes
 * glissent pour ouvrir le trou de dépôt (« reflow live ») → on voit le résultat
 * avant de lâcher. Confiné à la liste passée en `itemIds` (= une section).
 *
 * @param {Array<number|string>} itemIds  ids des lignes, dans l'ordre d'affichage
 * @param {(from:number, to:number) => void} onReorder  index source / cible
 *        (cible exprimée après retrait de l'élément déplacé, cf. splice)
 * @param {{ disabled?: boolean }} [opts]
 *
 * Renvoie :
 *  - setNode(id)         : callback ref à poser sur chaque ligne (mesure)
 *  - getHandleProps(id)  : props à étaler sur la poignée (pointer + clavier)
 *  - getRowStyle(id)     : style de transform de la ligne (soulèvement / glissement)
 *  - draggingId          : id de la ligne en cours de drag (ou null)
 */
export function useSortableLignes(itemIds, onReorder, opts = {}) {
	const { disabled = false } = opts
	const nodes = useRef(new Map())
	const snapshot = useRef([])
	const raf = useRef(null)
	const [drag, setDrag] = useState(null)
	// drag = { id, fromIndex, startY, deltaY, to, height }

	const setNode = useCallback((id) => (node) => {
		if (node) nodes.current.set(id, node)
		else nodes.current.delete(id)
	}, [])

	const computeTo = useCallback((snaps, fromIndex, movingCenter) => {
		// Index de dépôt = nombre de lignes (hors celle déplacée) dont le centre
		// passe au-dessus du centre courant de la ligne déplacée. C'est l'index
		// d'insertion APRÈS retrait (sémantique splice).
		let to = 0
		for (let i = 0; i < snaps.length; i++) {
			if (i === fromIndex) continue
			if (snaps[i].center < movingCenter) to++
		}
		return to
	}, [])

	const beginDrag = useCallback(
		(e, id) => {
			if (disabled) return
			if (e.button != null && e.button !== 0) return
			const fromIndex = itemIds.indexOf(id)
			if (fromIndex === -1) return
			e.preventDefault()
			const snaps = itemIds.map((it) => {
				const r = nodes.current.get(it).getBoundingClientRect()
				return {
					id: it,
					top: r.top,
					height: r.height,
					center: r.top + r.height / 2,
				}
			})
			snapshot.current = snaps
			setDrag({
				id,
				fromIndex,
				startY: e.clientY,
				deltaY: 0,
				to: fromIndex,
				height: snaps[fromIndex].height,
			})
			try {
				e.currentTarget.setPointerCapture(e.pointerId)
			} catch (_) {}
		},
		[disabled, itemIds],
	)

	const moveDrag = useCallback(
		(e) => {
			if (!drag) return
			const deltaY = e.clientY - drag.startY
			if (raf.current) return
			raf.current = requestAnimationFrame(() => {
				raf.current = null
				const snaps = snapshot.current
				const movingCenter = snaps[drag.fromIndex].center + deltaY
				const to = computeTo(snaps, drag.fromIndex, movingCenter)
				setDrag((d) => (d ? { ...d, deltaY, to } : d))
			})
		},
		[drag, computeTo],
	)

	const endDrag = useCallback(
		(e) => {
			if (!drag) return
			if (raf.current) {
				cancelAnimationFrame(raf.current)
				raf.current = null
			}
			const { fromIndex, to } = drag
			try {
				e.currentTarget.releasePointerCapture(e.pointerId)
			} catch (_) {}
			setDrag(null)
			if (to !== fromIndex) onReorder(fromIndex, to)
		},
		[drag, onReorder],
	)

	const onHandleKeyDown = useCallback(
		(e, id) => {
			if (disabled) return
			const i = itemIds.indexOf(id)
			if (i === -1) return
			if (e.key === "ArrowUp" && i > 0) {
				e.preventDefault()
				onReorder(i, i - 1)
			} else if (e.key === "ArrowDown" && i < itemIds.length - 1) {
				e.preventDefault()
				// to exprimé après retrait : insérer après le suivant = i + 1
				onReorder(i, i + 1)
			}
		},
		[disabled, itemIds, onReorder],
	)

	const getHandleProps = useCallback(
		(id) =>
			disabled
				? {}
				: {
						onPointerDown: (e) => beginDrag(e, id),
						onPointerMove: moveDrag,
						onPointerUp: endDrag,
						onPointerCancel: endDrag,
						onKeyDown: (e) => onHandleKeyDown(e, id),
						tabIndex: 0,
						role: "button",
						"aria-label": "Déplacer la ligne",
						style: { touchAction: "none", cursor: "grab" },
					},
		[disabled, beginDrag, moveDrag, endDrag, onHandleKeyDown],
	)

	const getRowStyle = useCallback(
		(id) => {
			if (!drag) return {}
			if (id === drag.id) {
				return {
					transform: `translateY(${drag.deltaY}px) scale(1.012)`,
					transition: "none",
					zIndex: 6,
					position: "relative",
					boxShadow: "0 12px 28px rgba(28, 25, 23, 0.16)",
					cursor: "grabbing",
				}
			}
			const i = itemIds.indexOf(id)
			const { fromIndex, to, height } = drag
			let shift = 0
			if (to >= fromIndex && i > fromIndex && i <= to) shift = -height
			else if (to < fromIndex && i >= to && i < fromIndex) shift = height
			return {
				transform: `translateY(${shift}px)`,
				transition: "transform 0.16s ease",
				position: "relative",
			}
		},
		[drag, itemIds],
	)

	return {
		setNode,
		getHandleProps,
		getRowStyle,
		draggingId: drag ? drag.id : null,
	}
}
